import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'

import LeftArrow from '@/assets/icons/left-arrow.svg?react'
import { Button } from '@/components/base/Button'
import { Divider } from '@/components/base/Divider'
import { Modal } from '@/components/base/Modal'
import { message } from '@/components/base/Notification'
import { Trans } from '@/components/Trans'
import { TeamRole } from '@/consts/team'
import { InstanceContextOverride } from '@/contexts/InstanceContext'
import { useTeamMemberInstances } from '@/pages/teams/member/hooks/useTeamMemberInstances'
import { useTeamStore } from '@/stores/team'
import { useTeamUserRole } from '@/stores/team'
import { useUserStore } from '@/stores/user'
import { formatDatePreset } from '@/utils/formatter'

import { deriveUsername, mapStatus, toISOSafe } from '../adapters/teamMember'
import { assignCredits } from '../services/member'
import { removeMember, suspendMembers, unSuspendMembers } from '../services/member'
import { AssignCreditDialog, MemberTarget } from './AssignCreditDialog'
import { MemberInfo } from './MemberInfo'
import { MemberInstancesTable } from './MemberInstancesTable'
import { StorageUsageBar } from './StorageUsageBar'

export function MemberDetailPage({ raw }: { raw: any }) {
  const userRole = useTeamUserRole()
  const { t } = useTranslation()
  const { id: routeId } = useParams<{ id: string }>()

  const teamResID = useTeamStore((s) => s.teamTokenData?.teamResID) ?? ''

  const [assignCreditOpen, setAssignCreditOpen] = useState(false)
  const userStore = useUserStore()
  const [adminBalance, setAdminBalance] = useState<number>(0)

  const [balanceCent, setBalanceCent] = useState<number>(raw?.account?.totalBonus?.moneyCent ?? 0)
  const [inactive, setInactive] = useState(
    String(mapStatus(raw?.user.status) ?? '').toLowerCase() === 'inactive',
  )
  const navigate = useNavigate()

  const instanceListState = useTeamMemberInstances({ teamResID, memberResID: routeId! })

  const user = raw?.user ?? {}
  const account = raw?.account ?? {}
  const id = routeId

  const [username, setUsername] = useState(deriveUsername(user) || id)
  const role = user.role
  const statusLabel = mapStatus(user.status)
  const bindStatus: boolean = user.bindParent
  const note = user.note ?? ''

  const totalSpendingCredits = (account.expense?.amount ?? 0) / 100

  const createdAtSec = user?.createdAt?.seconds ?? 0
  const lastLoginAtSec = user?.lastLoginAt?.seconds ?? 0
  const passwordUpdateAtSec = user?.passwordUpdateAt?.seconds ?? 0

  const lastLoginISO = toISOSafe(lastLoginAtSec)
  const lastLoginLocal = formatDatePreset(lastLoginISO, 'second')

  const loginAccount = user?.name ?? ''
  const totalAssignedInstances = raw?.totalAssignedInstances ?? 0
  const personalInstances = raw?.personalInstances ?? 0

  const isSelf = loginAccount === userStore.name
  const targetRole: TeamRole | undefined = user.role
  const canManageCredits =
    (userRole === TeamRole.Owner || userRole === TeamRole.Admin) &&
    !isSelf &&
    targetRole != null &&
    targetRole >= userRole

  const v = userStore.account.totalBonus.moneyCent ?? 0

  useEffect(() => {
    setAdminBalance(v)
  }, [v])

  const assignTarget: MemberTarget[] = useMemo(
    () => [{ id: id ?? '', name: username ?? '', currentCredit: balanceCent }],
    [id, username, balanceCent],
  )

  const memberInfo = {
    id,
    teamResID,
    username,
    role,
    status: statusLabel,
    bindStatus,
    note,
    balance: balanceCent,
    totalSpending: totalSpendingCredits,
    loginAccount,
    lastLogin: lastLoginLocal,
    totalAssignedInstances,
    personalInstances,
    createdAtSec,
    lastLoginAtSec,
    createTime: createdAtSec,
    passwordUpdateTime: passwordUpdateAtSec,
  }
  const handleDelete = async () => {
    const name = username || (id ?? '')

    try {
      await Modal.confirm({
        title: <Trans i18nKey="team.member.dialog.delete.title">Delete Account</Trans>,
        className: 'w-[480px]',
        content: (
          <div>
            <div className="mb-2">
              <Trans i18nKey="team.member.dialog.delete.content" values={{ name }}>
                Are you sure you want to delete <span className="text-highlight">{{ name }}</span>{' '}
                account?
              </Trans>
            </div>
            <div className="mt-2 text-error t-sm">
              <Trans i18nKey="team.member.dialog.delete.content2">
                This action cannot be undone.
              </Trans>
            </div>
          </div>
        ),
        confirmBtnCfg: {
          type: 'danger',
          text: <Trans i18nKey="team.member.dialog.delete.confirm">Delete Account</Trans>,
        },
      })

      await removeMember(teamResID, id ?? '')
      message.success(
        (
          <Trans i18nKey="team.member.memberInfo.toast.accountDeleted">Account deleted.</Trans>
        ) as any,
      )
      navigate('/team/member', { replace: true })
    } catch {
      console.warn('Cancel by user.')
    }
  }

  const handleSuspend = async () => {
    const name = username || (id ?? '')

    if (inactive) {
      try {
        await Modal.confirm({
          title: <Trans i18nKey="team.member.dialog.reactivate.title">Reactivate Account</Trans>,
          className: 'w-[480px]',
          content: (
            <Trans i18nKey="team.member.dialog.reactivate.content" values={{ name }}>
              Reactivate {{ name }}?
            </Trans>
          ),
          confirmBtnCfg: {
            type: 'primary',
            text: <Trans i18nKey="team.member.memberInfo.unsuspend">Unsuspend</Trans>,
          },
        })

        const { amountOfSuccess } = await unSuspendMembers([id ?? ''])
        if (amountOfSuccess > 0) {
          setInactive(false)
          message.success(
            (
              <Trans i18nKey="team.member.memberInfo.toast.accountUnsuspended">
                Account reactivated.
              </Trans>
            ) as any,
          )
        } else {
          message.error(
            (
              <Trans i18nKey="team.member.memberInfo.toast.accountUnsuspendFailed">
                Unsuspend failed.
              </Trans>
            ) as any,
          )
        }
      } catch {
        console.warn('Cancel by user.')
      }
      return
    }

    try {
      await Modal.confirm({
        title: <Trans i18nKey="team.member.dialog.suspend.title">Suspend Account</Trans>,
        className: 'w-[480px]',
        content: (
          <Trans i18nKey="team.member.dialog.suspend.content" values={{ name }}>
            Suspend {{ name }}?
          </Trans>
        ),
        confirmBtnCfg: {
          type: 'warning',
          text: t('team.member.dialog.suspend.confirm', 'Suspend'),
        },
      })

      const { amountOfSuccess } = await suspendMembers([id ?? ''])
      if (amountOfSuccess > 0) {
        setInactive(true)
        message.success(
          (
            <Trans i18nKey="team.member.memberInfo.toast.accountSuspended">
              Account suspended.
            </Trans>
          ) as any,
        )
      } else {
        message.error(
          (
            <Trans i18nKey="team.member.memberInfo.toast.accountSuspendedFailed">
              Suspend failed.
            </Trans>
          ) as any,
        )
      }
    } catch {
      console.warn('Cancel by user.')
    }
  }

  const handleAssignCredit = () => {
    setAssignCreditOpen(true)
  }

  const handleAssignConfirm = async ({
    perMemberAmount,
    memberIds,
    totalCost,
  }: {
    perMemberAmount: number
    memberIds: string[]
    totalCost: number
  }) => {
    try {
      if (!teamResID) throw new Error('No team context')
      await assignCredits(teamResID, memberIds, perMemberAmount)

      const deltaCent = Math.round(perMemberAmount * 100)
      if (memberIds.includes(id ?? '')) {
        setBalanceCent((prev) => prev + deltaCent)
      }
      setAdminBalance((prev) => Number((prev - totalCost).toFixed(3))) // 與 MemberPage 一樣的處理
      setAssignCreditOpen(false)
      message.success(
        t('team.member.memberInfo.toast.creditsAssigned', 'Assigned credits successfully.'),
      )
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <InstanceContextOverride memberResID={routeId ?? ''}>
      <div className="card bg-panel-white h-full overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div className="h6 flex items-center gap-2">
            <Link
              to={`/team/member`}
              className="w-11 h-11 icon-btn inline-flex items-center justify-center"
              aria-label="Back to members"
            >
              <LeftArrow />
            </Link>
            <span className="text-secondary">
              <Trans i18nKey="team.member.title">Member</Trans>
            </span>
            <span>/ {username}</span>
          </div>
          <div className="flex items-center gap-4">
            {userRole === TeamRole.Owner && !isSelf && (
              <Button type="warning" onClick={handleDelete}>
                <Trans i18nKey="team.member.memberInfo.delete">Delete</Trans>
              </Button>
            )}
            {canManageCredits && (
              <Button onClick={handleAssignCredit}>
                <Trans i18nKey="team.member.memberInfo.assignCredit">Assign Credits</Trans>
              </Button>
            )}
            {userRole === TeamRole.Owner && !isSelf && (
              <Button type={inactive ? 'default' : 'default'} onClick={handleSuspend}>
                {inactive ? (
                  <Trans i18nKey="team.member.memberInfo.unsuspend">Unsuspend</Trans>
                ) : (
                  <Trans i18nKey="team.member.memberInfo.suspend">Suspend</Trans>
                )}
              </Button>
            )}

            <Link
              to={`/team/member/${id}/permission`}
              state={{ member: raw, username }}
              className="bg-btn-secondary text-btn-foreground hover:bg-btn-secondary-hover px-4 py-3 items-center rounded-lg t-sb"
              aria-label={`View details of ${username} permission`}
            >
              <Trans i18nKey="team.member.memberInfo.editPermission">Edit Permission</Trans>
            </Link>
          </div>
        </div>
        <Divider />
        <MemberInfo
          data={{ ...memberInfo, id: id ?? '', username: username ?? '' }}
          onNicknameChange={setUsername}
          onBalanceChange={setBalanceCent}
        />
        <div className="mt-4">
          <StorageUsageBar memberId={id ?? ''} />
        </div>
        <div>
          <MemberInstancesTable
            page={instanceListState.page}
            setPage={instanceListState.setPage}
            total={instanceListState.total}
            instaceList={instanceListState.instaceList}
            loading={instanceListState.loading}
            domainInfos={instanceListState.domainInfos}
            createSnapshotInstance={instanceListState.createSnapshotInstance}
            setCreateSnapshotInstance={instanceListState.setCreateSnapshotInstance}
            getNodeList={instanceListState.getNodeList}
            onRelease={instanceListState.onRelease}
            onUpdate={instanceListState.onUpdate}
            pageSize={instanceListState.pageSize}
            teamResID={instanceListState.teamResID}
          />
        </div>
        <AssignCreditDialog
          open={assignCreditOpen}
          variant="single"
          adminBalance={adminBalance}
          targets={assignTarget}
          defaultAmount={0}
          onClose={() => setAssignCreditOpen(false)}
          onConfirm={handleAssignConfirm}
        />
      </div>
    </InstanceContextOverride>
  )
}
